/* Shifts — the three shifts as lines across the month, stage by stage as
 * columns, and the machines whose output depends on which shift has them. */

import { Blueprint, PanelHead } from '../../components/ui'
import { MachineDayLines } from '../../components/charts/MachineDayLines'
import { SHIFTS } from '../../lib/api_machines'
import type { Shift } from '../../lib/api_machines'
import { num } from '../../lib/format'
import { bandFill, dayNum, R, SHIFT_COLOR, unitName } from './parts'
import type { Ctx } from './parts'

const COL_H = 260       // the column chart's plot height
const COL_LO = 40       // and where its axis starts, as on every chart here

export function ShiftsTab({ c }: { c: Ctx }) {
  return (
    <>
      <ShiftLines c={c} />
      <ShiftColumns c={c} />
      <ShiftGaps c={c} />
    </>
  )
}

function ShiftLines({ c }: { c: Ctx }) {
  const { data } = c
  return (
    <Blueprint className="sheet">
      <PanelHead title="Shift efficiency, day by day" />
      <div style={{ fontSize: 12, color: 'var(--color-neutral-600)', marginTop: 4 }}>
        Actual against standard for each shift across the selection. Hover for the day's values.
      </div>
      <MachineDayLines days={c.days} height={240} strokeWidth={2} tipWidth={150}
        bench={c.bench} benchLabel={c.benchLabel}
        series={SHIFTS.map((s) => ({
          label: `Shift ${s}`, color: SHIFT_COLOR[s], values: data.shift_lines[s],
        }))}
        title={(i) => `${dayNum(data.dates[i])} ${c.monLong}`} />
    </Blueprint>
  )
}

function ShiftColumns({ c }: { c: Ctx }) {
  const { data } = c
  const rows = data.shift_rows

  // The axis runs to 120 unless a shift goes past it, then to the next ten.
  let colMax = 120
  rows.forEach((r) => SHIFTS.forEach((s) => {
    const v = r[s]
    if (v !== null) colMax = Math.max(colMax, Math.ceil(v / 10) * 10)
  }))
  const span = colMax - COL_LO
  const pos = (v: number) => `${(Math.max(0, Math.min(1, (v - COL_LO) / span)) * 100).toFixed(1)}%`
  const ticks = Array.from({ length: Math.floor(span / 20) + 1 }, (_, i) => COL_LO + i * 20)
    .concat(span % 20 ? [colMax] : [])
  const minWidth = Math.max(480, rows.length * 64)

  const all = data.shift_all
  const ran = SHIFTS.filter((s) => all[s] !== null)
  const ranked = [...ran].sort((a, b) => (all[b] as number) - (all[a] as number))
  const best = ranked[0]
  const worst = ranked[ranked.length - 1]
  const note = ran.length
    ? `Across the selection shift ${best} runs at ${num(all[best], 1)}% and shift ${worst} at ${num(all[worst], 1)}% — ${num((all[best] as number) - (all[worst] as number), 1)} points apart. Stage-level gaps below matter more than the plant average.`
    : 'No shift in this selection has a rated row.'

  return (
    <Blueprint className="sheet">
      <PanelHead title="Shift against shift" />
      <div style={{ fontSize: 12, color: 'var(--color-neutral-600)', marginTop: 4 }}>{note}</div>
      <div style={{
        display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 16, fontSize: 12,
        color: 'var(--color-neutral-800)',
      }}>
        {SHIFTS.map((s) => (
          <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{ width: 12, height: 12, display: 'block', background: SHIFT_COLOR[s] }} />
            Shift {s}
          </div>
        ))}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 7, color: 'var(--color-neutral-600)',
        }}>
          <span style={{
            width: 0, height: 12, borderLeft: '2px dashed var(--c-ink)', display: 'block',
          }} />Benchmark {c.benchLabel}
        </div>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: '34px minmax(0,1fr)', gap: 8, marginTop: 18,
      }}>
        <div className="num" style={{
          position: 'relative', height: COL_H, marginTop: 18, fontSize: 11,
          color: 'var(--color-neutral-600)',
        }}>
          {ticks.map((t) => (
            <span key={t} style={{
              position: 'absolute', right: 0, bottom: pos(t), transform: 'translateY(50%)',
            }}>{t}%</span>
          ))}
        </div>
        <div className="scroll-x" style={{ paddingTop: 18 }}>
          <div style={{
            position: 'relative', height: COL_H, minWidth,
            borderLeft: '1px solid var(--color-neutral-500)',
            borderBottom: '1px solid var(--color-neutral-500)',
          }}>
            {ticks.map((t) => (
              <span key={t} style={{
                position: 'absolute', left: 0, right: 0, bottom: pos(t),
                borderTop: '1px solid var(--c-grid)',
              }} />
            ))}
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end',
              gap: 10, padding: '0 8px',
            }}>
              {rows.map((r) => {
                const shifts = SHIFTS.filter((s) => r[s] !== null)
                return (
                  <div key={`${r.unit}-${r.stage}`} style={{
                    flex: '1 1 0', minWidth: 0, height: '100%', display: 'flex',
                    alignItems: 'flex-end', justifyContent: 'center', gap: 3,
                  }}>
                    {SHIFTS.map((s) => {
                      const v = r[s]
                      return (
                        <div key={s}
                          title={`${r.stage} · ${unitName(r.unit)} · Shift ${s}: ${v === null ? 'no data' : `${num(v, 1)}%`}`}
                          style={{
                            position: 'relative', flex: '1 1 0', maxWidth: 22,
                            height: v === null ? '0%' : pos(v), background: SHIFT_COLOR[s],
                            // The weakest shift of the stage at full strength.
                            opacity: s === r.weakest && shifts.length > 1 ? 1 : 0.8,
                          }}>
                          <span className="num" style={{
                            position: 'absolute', bottom: '100%', left: '50%',
                            transform: 'translateX(-50%)', marginBottom: 3, fontSize: 9,
                            lineHeight: 1, whiteSpace: 'nowrap', color: 'var(--color-neutral-700)',
                          }}>{v === null ? '' : num(v)}</span>
                        </div>
                      )
                    })}
                  </div>
                )
              })}
            </div>
            <span style={{
              position: 'absolute', left: 0, right: 0, bottom: pos(c.bench),
              borderTop: '2px dashed var(--c-ink)', zIndex: 1, pointerEvents: 'none',
            }} />
          </div>
          <div style={{ display: 'flex', gap: 10, padding: '6px 8px 0', minWidth }}>
            {rows.map((r) => (
              <div key={`${r.unit}-${r.stage}`} style={{ flex: '1 1 0', minWidth: 0, textAlign: 'center' }}>
                <div style={{ fontSize: 12, lineHeight: 1.25, textWrap: 'balance' } as React.CSSProperties}>
                  {r.stage}
                </div>
                <div style={{ fontSize: 10, color: 'var(--color-neutral-600)' }}>
                  {unitName(r.unit)} · spread {num(r.spread, 1)} pts
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Blueprint>
  )
}

function ShiftGaps({ c }: { c: Ctx }) {
  const byCode = new Map(c.data.machines.map((m) => [m.code, m]))
  const rows = c.data.shift_gaps.map((code) => byCode.get(code)!).filter(Boolean)

  const bar = (s: Shift, v: number | null) => (
    <div key={s} style={{
      display: 'grid', gridTemplateColumns: '16px minmax(0,1fr) 52px', alignItems: 'center', gap: 8,
    }}>
      <span style={{ fontSize: 11, color: 'var(--color-neutral-600)' }}>{s}</span>
      <svg viewBox="0 0 300 10" preserveAspectRatio="none"
        style={{ width: '100%', height: 10, display: 'block' }}>
        <rect x="0" y="0" width="300" height="10" fill="var(--c-track)" />
        {v !== null ? (
          <rect x="0" y="0" height="10" fill={bandFill(v, c.bench)}
            width={Math.max(1, Math.min(300, (v / 120) * 300))} />
        ) : null}
      </svg>
      <span className="num" style={{
        fontSize: 11, textAlign: 'right', color: 'var(--color-neutral-700)',
      }}>{v === null ? '–' : `${num(v, 1)}%`}</span>
    </div>
  )

  return (
    <Blueprint className="sheet">
      <PanelHead title="Machines that depend on who is running them" />
      <div style={{ fontSize: 12, color: 'var(--color-neutral-600)', marginTop: 4 }}>
        Largest gap between the best and worst shift on the same machine — a manning or
        handover signal, not a mechanical one.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 18 }}>
        {rows.map((m) => (
          <div key={m.code} style={{
            display: 'grid', gridTemplateColumns: '180px minmax(0,1fr) 108px',
            alignItems: 'center', gap: 16,
          }}>
            <button type="button" onClick={() => c.open(m.code)} style={{
              font: 'inherit', fontSize: 13, textAlign: 'left', background: 'none', border: 0,
              padding: 0, color: 'var(--color-neutral-800)', cursor: 'pointer',
            }}>{m.full_name}</button>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {SHIFTS.map((s) => bar(s, m.shifts[s]))}
            </div>
            <div style={R}>
              <span className="tag tag-outline num">{num(m.spread, 1)} pts</span>
            </div>
          </div>
        ))}
        {!rows.length ? (
          <div style={{ fontSize: 13, color: 'var(--color-neutral-700)' }}>
            No machine in this selection ran all three shifts.
          </div>
        ) : null}
      </div>
    </Blueprint>
  )
}
